import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, X, Check, Ban, Copy, ChevronRight,
  Mail, MessageSquare, Eye, EyeOff
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_ICON = {
  EMAIL: Mail,
  SMS: MessageSquare,
  BOTH: FileText,
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}

function DraftCard({ draft, onApprove, onReject, onCopy }) {
  const [expanded, setExpanded] = useState(false);
  const ChannelIcon = CHANNEL_ICON[draft.channel?.toUpperCase()] || FileText;
  const isCustomerUpdate = draft.action_type === "customer_update_draft";

  return (
    <div
      data-testid={`draft-card-${draft.user_id}`}
      className="bg-[#0F172A] border border-white/[0.07] rounded-md p-3"
    >
      {/* Header: ticket + channel + time */}
      <div className="flex items-center justify-between min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-bold text-[15px] text-white font-mono">
            {draft.ticket_number}
          </span>
          {draft.channel && (
            <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ color: "#7dd3fc", backgroundColor: "#7dd3fc14", border: "1px solid #7dd3fc30" }}>
              <ChannelIcon className="w-3 h-3" strokeWidth={2} />
              {draft.channel}
            </span>
          )}
          <span className="text-[11px] px-1.5 py-0.5 rounded font-semibold"
            style={{ color: "#a78bfa", backgroundColor: "#a78bfa14" }}>
            {isCustomerUpdate ? "Customer Update" : draft.action_type?.replace(/_/g, " ")}
          </span>
        </div>
        <span className="text-[11px] text-slate-500 shrink-0 ml-2">{timeAgo(draft.requested_at)}</span>
      </div>

      {/* Customer + device info */}
      <div className="flex items-center gap-2 mt-1.5 text-[12px] text-slate-400">
        {draft.customer_name && <span>{draft.customer_name}</span>}
        {draft.device_name && <span className="text-slate-600">|</span>}
        {draft.device_name && <span className="truncate">{draft.device_name}</span>}
        {draft.ticket_status && (
          <>
            <span className="text-slate-600">|</span>
            <span className="text-slate-500">{draft.ticket_status}</span>
          </>
        )}
      </div>

      {/* Drafted by */}
      <div className="text-[11px] text-slate-500 mt-1">
        Drafted by: <span className="text-slate-400 font-medium">{draft.user_name || draft.user_id}</span>
      </div>

      {/* Preview / Full text */}
      <div className="mt-2 bg-slate-900/50 rounded px-2.5 py-2 border border-white/[0.04]">
        <p className="text-[13px] text-slate-300 leading-relaxed whitespace-pre-wrap">
          {expanded ? draft.full_text : draft.preview}
          {!expanded && draft.full_text?.length > 120 && "..."}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-2.5">
        <button
          data-testid={`draft-expand-${draft.user_id}`}
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-200 transition-colors px-2 py-1 rounded hover:bg-white/[0.04]"
        >
          {expanded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {expanded ? "Collapse" : "View Full"}
        </button>
        <button
          data-testid={`draft-copy-${draft.user_id}`}
          onClick={() => onCopy(draft)}
          className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-white/[0.04]"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy
        </button>
        <div className="flex-1" />
        <button
          data-testid={`draft-reject-${draft.user_id}`}
          onClick={() => onReject(draft)}
          className="flex items-center gap-1 text-[11px] font-bold text-red-400 hover:text-red-300 transition-colors px-2.5 py-1 rounded hover:bg-red-500/10 border border-red-500/20"
        >
          <Ban className="w-3.5 h-3.5" />
          Reject
        </button>
        <button
          data-testid={`draft-approve-${draft.user_id}`}
          onClick={() => onApprove(draft)}
          className="flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors px-2.5 py-1 rounded hover:bg-emerald-500/10 border border-emerald-500/20"
        >
          <Check className="w-3.5 h-3.5" />
          Approve
        </button>
      </div>
    </div>
  );
}

export function DraftsPanel() {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sam/skills/pending-drafts`);
      setDrafts(data.drafts || []);
    } catch {
      setDrafts([]);
    }
    setLoading(false);
  }, []);

  // Initial fetch for badge count + poll when open
  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  useEffect(() => {
    if (!open) return;
    fetchDrafts();
    const iv = setInterval(fetchDrafts, 15000);
    return () => clearInterval(iv);
  }, [open, fetchDrafts]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (draft) => {
    try {
      await axios.post(`${API}/sam/skills/execute-approved`, {
        user_id: draft.user_id,
        approver_id: "dashboard_operator",
        approver_name: "Dashboard Operator",
      });
      showToast(`Approved: ${draft.ticket_number}`);
      fetchDrafts();
    } catch {
      showToast("Approve failed", "error");
    }
  };

  const handleReject = async (draft) => {
    try {
      await axios.post(`${API}/sam/skills/reject-draft`, {
        user_id: draft.user_id,
      });
      showToast(`Rejected: ${draft.ticket_number}`);
      fetchDrafts();
    } catch {
      showToast("Reject failed", "error");
    }
  };

  const handleCopy = (draft) => {
    // Extract just the message body for customer_update_draft
    let text = draft.full_text || draft.preview;
    if (text.includes("---\n")) {
      text = text.split("---\n").slice(1).join("---\n");
    }
    navigator.clipboard.writeText(text).then(() => {
      showToast("Copied to clipboard");
    }).catch(() => {
      showToast("Copy failed", "error");
    });
  };

  return (
    <>
      {/* Toggle button — fixed on right edge */}
      <button
        data-testid="drafts-panel-toggle"
        onClick={() => setOpen(!open)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-1 px-1.5 py-3 rounded-l-md transition-colors"
        style={{
          backgroundColor: drafts.length > 0 ? "#7c3aed" : "#1e293b",
          borderLeft: "2px solid",
          borderColor: drafts.length > 0 ? "#a78bfa" : "#334155",
        }}
      >
        <ChevronRight
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: drafts.length > 0 ? "#e9d5ff" : "#94a3b8" }}
          strokeWidth={2.5}
        />
        {drafts.length > 0 && (
          <span className="text-[11px] font-bold text-white writing-mode-vertical"
            style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}>
            {drafts.length} DRAFT{drafts.length > 1 ? "S" : ""}
          </span>
        )}
      </button>

      {/* Slide-out panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="drafts-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 h-screen w-[380px] z-50 flex flex-col border-l border-white/[0.08]"
            style={{ backgroundColor: "#0a0f1a" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-400" strokeWidth={2} />
                <span className="text-sm font-bold text-white tracking-wide">PENDING DRAFTS</span>
                <span className="text-[11px] font-bold text-violet-400 bg-violet-500/15 px-1.5 py-0.5 rounded">
                  {drafts.length}
                </span>
              </div>
              <button
                data-testid="drafts-panel-close"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" strokeWidth={2} />
              </button>
            </div>

            {/* Draft list */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
              {loading && drafts.length === 0 && (
                <div className="text-center text-slate-600 text-sm py-8">Loading...</div>
              )}
              {!loading && drafts.length === 0 && (
                <div className="text-center text-slate-600 text-sm py-8">No pending drafts</div>
              )}
              {drafts.map((d, i) => (
                <DraftCard
                  key={`${d.user_id}-${d.requested_at}-${i}`}
                  draft={d}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onCopy={handleCopy}
                />
              ))}
            </div>

            {/* Toast */}
            <AnimatePresence>
              {toast && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className={`absolute bottom-4 left-4 right-4 text-center text-[13px] font-semibold py-2 rounded-md ${
                    toast.type === "error" ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"
                  }`}
                >
                  {toast.msg}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
